import { Link, useNavigate, Navigate } from 'react-router-dom'
import { Plus, Star, Sparkles, LayoutGrid } from 'lucide-react'
import type { ProviderServiceItem } from 'shared'
import { categoryLabel } from 'shared'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/skeleton'
import { BrandMark } from '../../components/BrandMark'
import { ThemeToggle } from '../../components/ThemeToggle'
import { useSession, signOut } from '@/lib/auth-client'
import { useProviderProfile } from '@/hooks/useProviderProfile'

const ProviderHome = () => {
  const { data: session, isPending: sessionPending } = useSession()
  const { data, isLoading } = useProviderProfile()

  if (sessionPending) return <DashboardShell><LoadingState /></DashboardShell>
  if (!session) return <Navigate to="/login" replace />

  const provider = data?.provider ?? null

  return (
    <DashboardShell userName={session.user.name}>
      {isLoading ? <LoadingState /> : provider ? <Dashboard provider={provider} /> : <EmptyProfile />}
    </DashboardShell>
  )
}

function DashboardShell({ children, userName }: { children: React.ReactNode; userName?: string }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <BrandMark to="/enter" />
          <div className="flex-1" />
          {userName && <span className="text-sm text-muted-foreground hidden sm:inline">{userName}</span>}
          <Button variant="outline" size="sm" onClick={() => signOut()}>
            Sign out
          </Button>
          <ThemeToggle />
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 py-8">{children}</div>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-9 w-64" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-40 rounded-2xl" />
        ))}
      </div>
    </div>
  )
}

function EmptyProfile() {
  const navigate = useNavigate()
  return (
    <div className="flex flex-col items-center text-center py-16 max-w-md mx-auto">
      <div className="w-16 h-16 rounded-2xl bg-primary/15 text-primary flex items-center justify-center mb-6">
        <Sparkles className="size-8" />
      </div>
      <h1 className="text-2xl font-bold mb-2">Become a provider</h1>
      <p className="text-muted-foreground mb-8">
        Set up your provider profile to list services and start getting bookings on Bookmi.
      </p>
      <Button size="lg" onClick={() => navigate('/provider/onboarding')}>
        Set up my profile
      </Button>
    </div>
  )
}

function Dashboard({ provider }: { provider: { name: string; services: ProviderServiceItem[] } }) {
  const navigate = useNavigate()
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Your services</h1>
          <p className="text-muted-foreground text-sm">Manage the services you offer as {provider.name}.</p>
        </div>
        <Button onClick={() => navigate('/provider/services/new')}>
          <Plus className="size-4" />
          Add service
        </Button>
      </div>

      {provider.services.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center text-center py-14">
            <div className="w-12 h-12 rounded-xl bg-muted text-muted-foreground flex items-center justify-center mb-4">
              <LayoutGrid className="size-6" />
            </div>
            <p className="font-medium mb-1">No services yet</p>
            <p className="text-muted-foreground text-sm mb-6">Add your first service to appear in search.</p>
            <Button onClick={() => navigate('/provider/services/new')}>
              <Plus className="size-4" />
              Add your first service
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {provider.services.map((s) => (
            <ProviderServiceCard key={s.id} service={s} />
          ))}
        </div>
      )}
    </div>
  )
}

function ProviderServiceCard({ service }: { service: ProviderServiceItem }) {
  return (
    <Link to={`/services/${service.id}`} className="block">
      <Card className="h-full transition-shadow hover:ring-foreground/20 hover:shadow-md">
        {service.imageUrl ? (
          <img src={service.imageUrl} alt={service.title} className="h-32 w-full object-cover" />
        ) : (
          <div className="h-32 bg-gradient-to-br from-primary/10 to-accent/10 rounded-t-xl" />
        )}
        <CardContent>
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="font-semibold truncate">{service.title}</h3>
            <div className="flex items-center gap-1 text-amber-500 shrink-0">
              <Star className="size-4 fill-current" />
              <span className="text-sm text-foreground">{service.rating.toFixed(1)}</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-3">{categoryLabel(service.category)}</p>
          <p className="text-sm font-medium">From ${service.priceFrom}</p>
        </CardContent>
      </Card>
    </Link>
  )
}

export default ProviderHome
